/* eslint-disable no-cond-assign */
// Third-party
import get from 'lodash/get';

// Types
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { ComparisonItemDAO } from '../ProposalItemDAO';

// Utils
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { LOCALE } from '../../../../utils/i18n';
import { PercentageCell } from '../../../../views/components/cells';
import Nullable from '@/types/nullable';
import DonutData from '@/types/donut-data';
import CustomAllocation from '@/types/custom-allocation';

/**
 * @class GenericAllocations Data Access Object
 */
export default class GenericAllocationsDAO extends ComparisonItemBasedProposalDAO {
  colorScheme?: string;

  precision?: number = 2;

  showOtherField?: boolean = true;

  forceFullAllocation: boolean = false;

  protected getColorKeys(): string[] {
    const daoConfig: ProposalNS.GenericAllocationsConfig = this.proposalElement.DAO as ProposalNS.GenericAllocationsConfig;

    if (daoConfig.colorScheme) {
      return [daoConfig.colorScheme];
    }

    return [];
  }

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new GenericAllocationsDAO(proposal, proposalElement);
  }

  protected build(): void {
    let itemDAO: ComparisonItemDAO | null = null;
    const daoConfig: ProposalNS.GenericAllocationsConfig = this.proposalElement.DAO as ProposalNS.GenericAllocationsConfig;

    if (daoConfig.numberPrecision !== undefined) {
      this.precision = daoConfig.numberPrecision;
    }

    if (daoConfig.showOtherField !== undefined) {
      this.showOtherField = daoConfig.showOtherField;
    }

    if (daoConfig.forceFullAllocation !== undefined) {
      this.forceFullAllocation = daoConfig.forceFullAllocation;
    }

    while ((itemDAO = this.nextItem()) !== null) {
      const allocations = get(itemDAO.item, daoConfig.allocationsPath, null) as Nullable<CustomAllocation>;
      let total = daoConfig.fields.reduce((total, daoField) => {
        if (allocations) {
          total += (allocations.data.find(a => a.key === daoField.key)?.value || 0) * 100;
        }

        return total + 0;
      }, 0);

      for (const daoField of daoConfig.fields) {
        const fieldName = this.formatLocalizedString(daoField.label);
        this.addFieldName(fieldName, `${ProposalStyleClassNames.ProposalTableContent} ${daoField.key}`);

        let value: number;

        if (allocations == null) {
          value = 0;
        } else {
          value = (allocations.data.find(a => a.key === daoField.key)?.value || 0) * 100;

          // Some proposals never display the 'Other' category. In that case we might want to ensure that
          // the total allocation percentage adds up to 100%
          if (total < 100 && this.forceFullAllocation) {
            value = value = 100 * (value / total);
          }

          this.addAllocationsAsAtDisclaimer(itemDAO, daoConfig);
          this.addDataProviderDisclaimer(itemDAO, allocations.provider);
        }

        this.addOtherDisclaimer();

        const field = new ProposalFieldDAO(fieldName, value.toFixed(this.precision), ProposalStyleClassNames.ProposalTableContent);
        field.key = daoField.key;
        field.cellType = PercentageCell;
        field.decimalPlace = this.precision;
        this.addFieldDAO(itemDAO, field);
      }

      if (this.forceFullAllocation) {
        total = 100;
      }

      if (this.showOtherField) {
        const otherFieldName = `${this.formatMessage('Common.Other')}*`;
        const field = new ProposalFieldDAO(otherFieldName, (100 - total).toFixed(this.precision), ProposalStyleClassNames.ProposalTableContent);
        field.key = 'OTHER';
        field.cellType = PercentageCell;
        field.decimalPlace = this.precision;
        this.addFieldName(otherFieldName, `${ProposalStyleClassNames.ProposalTableContent} ${field.key}`);
        this.addFieldDAO(itemDAO, field);
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }

  private addAllocationsAsAtDisclaimer(itemDAO: ComparisonItemDAO, daoConfig: ProposalNS.GenericAllocationsConfig) {
    this.addDisclaimerConfigs([
      {
        key: PROPOSAL_DISCLAIMERS.CUSTOM_ALLOCATIONS,
        items: [itemDAO.item],
        config: {
          doNotMergeOnPage: true,
          datesPath: `${daoConfig.allocationsPath}.asAt`,
        },
      },
    ]);
  }

  private addOtherDisclaimer() {
    this.addDisclaimerConfigs([
      {
        key: PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER,
        items: [],
        config: {
          doNotMergeOnPage: true,
        },
      },
    ]);
  }

  private addDataProviderDisclaimer(itemDAO: ComparisonItemDAO, provider: string) {
    const disclaimer = this.formatMessage('Disclaimers.DataProvidedBy', { provider: this.formatMessage(`Providers.${provider}`) });

    this.addDisclaimerConfigs([
      {
        key: PROPOSAL_DISCLAIMERS.CUSTOM_DISCLAIMER,
        items: [itemDAO.item],
        config: {
          doNotMergeOnPage: true,
          customString: {
            [LOCALE.EN_CA]: disclaimer,
            [LOCALE.FR_CA]: disclaimer,
          },
        },
      },
    ]);
  }
}
